import { useEffect, useState } from "react";
import { X, BarChart3, Wrench, Network, TriangleAlert, Trophy, Cable, Users, Split } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import api from "@/lib/api";

const monthNow = () => new Date().toISOString().slice(0, 7);

export default function StatsPanel({ role, onClose }) {
  const { t } = useI18n();
  const [month, setMonth] = useState(monthNow());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bal, setBal] = useState(null);

  const load = (mo) => {
    setLoading(true);
    api.get("/stats/technicians", { params: { month: mo } })
      .then((r) => setRows(r.data.rows || [])).catch(() => setRows([])).finally(() => setLoading(false));
  };
  useEffect(() => { load(month); /* eslint-disable-next-line */ }, [month]);
  useEffect(() => { api.get("/stats").then((r) => setBal(r.data)).catch(() => {}); }, []);

  return (
    <div className="absolute inset-0 bg-slate-900 flex flex-col" data-testid="stats-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
        <div className="flex items-center gap-2 font-heading font-bold text-white"><BarChart3 size={18} className="text-cyan-400" /> {t("Statystyki miesiąca (premie)")}</div>
        <button onClick={onClose} data-testid="stats-close" className="text-slate-400 hover:text-white"><X size={20} /></button>
      </div>
      <div className="px-4 py-3 border-b border-slate-800 flex items-center gap-2">
        <label className="text-xs text-slate-400">{t("Miesiąc:")}</label>
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="stats-month" className="bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-sm text-white" />
      </div>
      <div className="flex-1 overflow-y-auto p-4">
        {bal && (
          <div className="mb-4" data-testid="network-balance-card">
            <div className="text-xs uppercase tracking-wide text-slate-500 mb-2 font-heading">{t("Bilans sieci")}</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="rounded-lg border border-slate-700 bg-slate-800/50 p-3" data-testid="balance-km">
                <Cable size={15} className="text-blue-400" />
                <div className="text-xl font-heading font-bold text-white mt-1">{bal.cable_total_km ?? 0} <span className="text-sm text-slate-400">km</span></div>
                <div className="text-[11px] text-slate-500">{t("Długość kabli")}</div>
              </div>
              <div className="rounded-lg border border-slate-700 bg-slate-800/50 p-3" data-testid="balance-clients">
                <Users size={15} className="text-emerald-400" />
                <div className="text-xl font-heading font-bold text-white mt-1">{bal.counts?.clients ?? 0}</div>
                <div className="text-[11px] text-slate-500">{t("Klienci")}</div>
              </div>
              <div className="rounded-lg border border-slate-700 bg-slate-800/50 p-3" data-testid="balance-fibers">
                <Split size={15} className="text-cyan-400" />
                <div className="text-xl font-heading font-bold text-white mt-1">{bal.free_fibers ?? 0}<span className="text-sm text-slate-400">/{bal.total_fibers ?? 0}</span></div>
                <div className="text-[11px] text-slate-500">{t("Wolne włókna")}</div>
              </div>
              <div className="rounded-lg border border-slate-700 bg-slate-800/50 p-3" data-testid="balance-faults">
                <TriangleAlert size={15} className="text-amber-400" />
                <div className="text-xl font-heading font-bold text-white mt-1">{bal.active_faults ?? 0}</div>
                <div className="text-[11px] text-slate-500">{t("Aktywne awarie")}</div>
              </div>
            </div>
            <div className="border-b border-slate-800 mt-4" />
          </div>
        )}
        {loading ? <div className="text-sm text-slate-500">Ładowanie…</div> : rows.length === 0 ? (
          <div className="text-sm text-slate-500 text-center py-10">{t("Brak danych za ten miesiąc.")}</div>
        ) : (
          <div className="space-y-2">
            {rows.map((r, i) => (
              <div key={r.technician} className={`rounded-lg border p-3 ${i === 0 && role === "admin" ? "border-amber-600/50 bg-amber-950/15" : "border-slate-700 bg-slate-800/40"}`} data-testid={`stats-row-${i}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-white flex items-center gap-1.5">{i === 0 && role === "admin" && <Trophy size={14} className="text-amber-400" />}{r.technician}</span>
                  <span className="text-lg font-heading font-bold text-cyan-300">{r.total}</span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="rounded bg-slate-900/60 py-1.5"><Wrench size={13} className="mx-auto text-emerald-400" /><div className="text-sm font-mono text-white mt-0.5">{r.installs}</div><div className="text-[10px] text-slate-500">{t("Montaże")}</div></div>
                  <div className="rounded bg-slate-900/60 py-1.5"><Network size={13} className="mx-auto text-blue-400" /><div className="text-sm font-mono text-white mt-0.5">{r.network}</div><div className="text-[10px] text-slate-500">{t("Prace na sieci")}</div></div>
                  <div className="rounded bg-slate-900/60 py-1.5"><TriangleAlert size={13} className="mx-auto text-amber-400" /><div className="text-sm font-mono text-white mt-0.5">{r.faults}</div><div className="text-[10px] text-slate-500">{t("Awarie")}</div></div>
                </div>
              </div>
            ))}
          </div>
        )}
        <p className="text-[11px] text-slate-500 mt-4">{t("Liczone z rejestru zmian: montaże = nowi klienci, prace na sieci = operacje na infrastrukturze (kable/mufy/splittery/spawy/słupy/trasy/przełącznice/OLT), awarie = zgłoszenia rozwiązane w danym miesiącu.")}</p>
      </div>
    </div>
  );
}
